import * as members from '../../privates/privateMembers';
import {validate} from '../../privates/validations/validation';

/**
 * @mixin FocusMixin
 * @summary Provides functionality for elements that can set and lose focus.
 *
 *  The element on the page that is currently active is the element in focus.
 *  Only one element on the page can have focus at any given moment.
 *  Typically, an element exhibits a visual cue, such as a subtle outline,
 *  indicating that it is in focus. The element in focus receives keystroke
 *  events if the user causes any.
 *
 *  An element receives focus and loses focus (blurs) through user actions,
 *  such as clicking and tabbing, or programmatically, using the
 *  [focus( )](#focus) and [blur( )](#blur) functions.
 * @memberof $w
 */
class FocusMixin {

    /**
     * @function focus
     * @syntax
     * function focus(): void
     * @summary Places focus on the element.
     * @description
     *  The `focus()` function places focus on the element and fires a `focus`
     *  event.
     *
     *  The `focus` event handlers set on this element by the [onFocus( )](#onFocus)
     *  function or in the Editor are called.
     *
     *  Receiving focus through a call to this function is equivalent to
     *  a user clicking on or tabbing to the element manually.
     * @snippet [FocusMixin-focus.es6=Place focus on an element]
     * @see [onFocus( )](#onFocus)
     * @see [blur( )](#blur)
     * @see [onBlur( )](#onBlur)
     * @memberof $w.FocusMixin
     */
    focus(){
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        RMI.executeCompBehavior(compId, 'focus', {requiresDom: {compId, requiresFullRender: true}}, () => {});
    }

    /**
     * @function blur
     * @syntax
     * function blur(): void
     * @summary Removes focus from the element.
     * @description
     *  The `blur()` function removes focus from the element and fires a `blur`
     *  event.
     *
     *  The `blur` event handlers set on this element by the [onBlur( )](#onBlur)
     *  function or in the Editor are called.
     *
     *  Removing focus through a call to this function is equivalent to a user
     *  clicking on another element or tabbing out of the element manually.
     *
     *  If blur() is called on an element that is not in focus, it has no effect.
     *  The element in focus remains in focus and the `onBlur` event handlers
     *  are not called.
     * @snippet [FocusMixin-blur.es6=Remove focus from an element]
     * @see [onBlur( )](#onBlur)
     * @see [focus( )](#focus)
     * @see [onFocus( )](#onFocus)
     * @memberof $w.FocusMixin
     */
    blur(){
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);

        RMI.executeCompBehavior(compId, 'blur', {requiresDom: {compId, requiresFullRender: true}}, () => {});
    }

    /**
     * @function onFocus
     * @syntax
     * function onFocus(handler: EventHandler): Element
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when the element receives focus.
     * @description
     *  An element receives focus through user actions,
     *  such as clicking and tabbing, or programmatically, using the
     *  [focus( )](#focus) function.
     *
     * @note
     *  `onFocus()` has no effect when applied to [RadioButtonGroup]($w.RadioButtonGroup.html) elements.
     *
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the element receives focus.
     * @returns {$w.Element} The element on which the event is now registered.
     * @snippet [FocusMixin-onFocus.es6=Get the ID of the element that has received focus]
     * @see [focus( )](#focus)
     * @see [onBlur( )](#onBlur)
     * @see [blur( )](#blur)
     * @memberof $w.FocusMixin
     * @eventtype focus
     * @eventhandler
     */
    onFocus(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onFocus',
            dataToValidate: {onFocus: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const controllerId = members._controllerId.get(this);
        const contextId = members._contextId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().FOCUS, controllerId, cb);
        return this;
    }

    /**
     * @function onBlur
     * @syntax
     * function onBlur(handler: EventHandler): Element
     * callback EventHandler(event: Event, $w: Function): void
     * @summary Adds an event handler that runs when the element loses focus.
     * @description
     *  An element loses focus (blurs) through user actions,
     *  such as clicking and tabbing, or programmatically, using the
     *  [blur( )](#blur) function.
     *
     * @note
     * `onBlur()` has no effect when applied to [RadioButtonGroup]($w.RadioButtonGroup.html) elements.
     *
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @param {$w.EventHandler} handler The name of the function or
     *  the function expression to run when the element losed focus.
     * @returns {$w.Element} The element on which the event is now registered.
     * @snippet [FocusMixin-onBlur.es6=Get the ID of the element that has lost focus]
     * @see [blur( )](#blur)
     * @see [onFocus( )](#onFocus)
     * @see [focus( )](#focus)
     * @memberof $w.FocusMixin
     * @eventtype blur
     * @eventhandler
     */
    onBlur(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onBlur',
            dataToValidate: {onBlur: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().BLUR, controllerId, cb);
        return this;
    }
}

export default FocusMixin;
